<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="(showLoader && !showFilters) || (!getOperationApprovalsList.length && !showLoader && !applyErrorState && !showFilters)"
    :showFiltersAndEmptyState="!getOperationApprovalsList.length && !showLoader && !applyErrorState && showFilters"
    :showLoader="showLoader"
    :applyErrorState="applyErrorState"
    :errorStateMessage="t('approvals.signed.emptyState.operationsError')"
    :showFloatingButton="showFilters && !applyErrorState"
    :floatingButtonParams="{ icon: 'filter', label: t('filters.filter')}"
    :showTopRightAreaSlot="renderAccountSelector"
    @floatingButtonClicked="showFiltersModal = true"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- *********************************************
        *                 Account Selector               *
        ********************************************** -->
        <template #accounts-card-slot-area>
            <account-selector
            v-if="renderAccountSelector"
            :class="{ 'mobile-margin': getMobileAppState }"
            :selectedOption="selectedAccountId"
            :options="getAccountsBalances"
            @clicked="[selectedAccountId = $event, showFilters = false, closeAllFilters()]"/>
        </template>
        <!-- *********************************************
        *                  Visible Filters               *
        ********************************************** -->
        <consult-filters-wrapper
        v-if="showFilters"
        :filterTags="filterTags"
        :applyButtonDisabled="applyButtonDisabled"
        @applyFilters="applyFilters(appliedFilters)"
        @removeFilterTag="removeFilterTag"
        @closeAllFilters="closeAllFilters">
            <!-- Double Calendar -->
			<input-double-calendar
			calendarDirection="row"
			:fromDateParams="beginDateParams"
			:toDateParams="endDateParams"
			@inputFromDate="appliedFilters.beginDate = $event"
			@inputToDate="appliedFilters.endDate = $event"/>
        </consult-filters-wrapper>
        <!-- *********************************************
		*												 *
		*                       Table                    *
		*												 *
		********************************************** -->
        <transition name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && !applyErrorState" class="content-wrapper prevent-auto-margins" :class="{'with-filters': !!filterTags.length}">
                <operations-list
                v-if="getOperationApprovalsList.length"
                :operationsList="getOperationApprovalsList"
                :hasMore="getApprovalsHasMore"
                :isLoadingMore="tableLoadingMore"
                :loadingMoreText="t('approvals.signed.loadingMoreText')"
                :isMobile="getMobileAppState"
                @loadMore="loadMoreHandler"
                @rowClick="openDetailsHandler" />
            </div>
        </transition>
        <!-- *********************************************
        *                    Empty State                 *
        ********************************************** -->
		<template v-if="!showLoader && !applyErrorState && !getOperationApprovalsList.length" #empty-state-slot>
            <empty-state-generic
            illustration="emptyState/approvals"
            :title="t('approvals.signed.emptyState.generic.title')"
            :description="emptyStateDescription"/>
        </template>
    </wrapper-features-templates-one-column>
    <!-- Filters Modal -->
    <filters-modal
    v-if="showFiltersModal"
    :appliedFilters="appliedFilters"
    :statusList="getOperationsStatusList"
    :operationTypesList="getOperationsTypesList"
    :beginDateParams="beginDateParams"
    :endDateParams="endDateParams"
    @hideMe="showFiltersModal = false"
    @applyFilters="applyFilters"/>
    <!-- Details -->
    <details-base
    v-if="showOperationDetails"
    :operationDetailsInfo="operationDetailsInfo"
    :showDetailsFooter="operationDetailsInfo.status.id === 7"
    :isMobile="getMobileAppState"
    @hideMe="showOperationDetails = false"
    @success="successHandler"
    @updateList="[showOperationDetails = false, resetPaginationParams(), requestOperationApprovals()]"/>
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import AccountSelector from '@/components/generic/selectorsWithDetails/Accounts'
import ConsultFiltersWrapper from '@/components/wrappers/ConsultFiltersWrapper'
import InputDoubleCalendar from '@/components/generic/inputs/DoubleCalendar'
import OperationsList from '@/components/features/approvals/OperationsList'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import FiltersModal from '@/components/features/approvals/signed/FiltersModal'
import DetailsBase from '@/components/features/approvals/details/DetailsBase'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* Vue */
import { ref, computed, onBeforeUnmount } from 'vue'

/* Aux */
import { getDate, createJSONDateEndDay } from '@/assets/scripts/dateBuilders'
import { clearObject } from '@/assets/scripts/objects'
import { sucessObjectConstrutor, errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const {
    requestAccountsBalances,
    requestOperationApprovalsList,
    requestOperationsStatusList,
    requestOperationsTypes,
    clearOperationApprovalsList
} = mapActions(moduleMapper.APPROVALS_SIGNED)
const {
    getAccountsBalances,
    getOperationApprovalsList,
    getOperationsStatusList,
    getOperationsTypesList,
    getApprovalsHasMore
} = mapGetters(moduleMapper.APPROVALS_SIGNED)
const { getMobileAppState } = mapGetters()

/* Show Data */
const showLoader = ref(false)
const showOperationDetails = ref(false)

/* Data */
const applyErrorState = ref(false)
const renderAccountSelector = ref(false)
const selectedAccountId = ref(null)

/*************************************************
*                                                *
*                Pagination Params               *
*                                                *
*************************************************/
const tableLoadingMore = ref(false)
const paginationParams = ref({})

const resetPaginationParams = () => {
    paginationParams.value = {
        pageNumber: 1
    }
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = (data) => {
	feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    applyErrorState.value = true

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*                  Filter Modal                  *
*                                                *
*************************************************/
const showFilters = ref(false)
const showFiltersModal = ref(false)
const filterTags = ref([])

const appliedFilters = ref({
    beginDate: null,
    endDate: null,
    operationTypeId: null,
    operationStatus: null
})

const hasFilters = computed(() => {
    return filterTags.value.length !== 0
})

/* Begin Date Params */
const beginDateParams = computed(() => {
    return {
        labelText: t('approvals.signed.modal.filters.beginDate'),
        placeholder: t('approvals.signed.modal.filters.beginDate'),
        inputValue: appliedFilters.value.beginDate,
        disabledDates: { from: new Date() }
    }
})

/* End Date Params */
const endDateParams = computed(() => {
    return {
        labelText: t('approvals.signed.modal.filters.endDate'),
        placeholder: t('approvals.signed.modal.filters.endDate'),
        inputValue: appliedFilters.value.endDate,
        disabledDates: { from: new Date() }
    }
})

const applyButtonDisabled = computed(() => {
    return !appliedFilters.value.beginDate && !appliedFilters.value.endDate
})

const applyFilters = (filters) => {
    filterTags.value = []
    appliedFilters.value = filters

    for (const filter in filters) {
		if (filters[filter]) {
            filterTags.value.push({
                id: filter,
                label: t(`approvals.signed.modal.filters.${filter}`),
                text: filterTagTextHandler(filter, filters[filter])
            })
        } else {
			appliedFilters.value[filter] = null
		}
	}

    resetPaginationParams()
    requestOperationApprovals()
}

const removeFilterTag = (tagId) => {
    appliedFilters.value[tagId] = null
	filterTags.value = filterTags.value.filter(filter => filter.id !== tagId)

    if (!filterTags.value.length) filterTags.value = []
    resetPaginationParams()
    requestOperationApprovals()
}

const closeAllFilters = () => {
    filterTags.value = []
    appliedFilters.value = clearObject(appliedFilters.value)
    resetPaginationParams()
    requestOperationApprovals()
}

const filterTagTextHandler = (filter, value) => {
	switch (filter) {
		case 'beginDate':
		case 'endDate':
			return getDate(value)
		case 'operationTypeId':
			return getOperationsTypesList.value.find(type => type.id === value).type
        case 'operationStatus':
            return getOperationsStatusList.value.find(status => status.id === value).description
		default: return value
	}
}

/*************************************************
*                                                *
*                  Empty State                   *
*                                                *
*************************************************/

const emptyStateDescription = computed(() => {
    return !hasFilters.value ? t('approvals.signed.emptyState.generic.description') : t('approvals.signed.emptyState.noFilteredResults.description')
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const initialRequest = async () => {
    resetPaginationParams()

    showFilters.value = false
    showLoader.value = true
    applyErrorState.value = false
    showFeedbackToast.value = false

    try {
        await requestAccountsBalance()
        await requestOperationsStatusList()
        await requestOperationsTypes({ isSigned: true })
        await requestOperationApprovals()

        if (getOperationApprovalsList.value.length) showFilters.value = true

        showLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, initialRequest)
    }
}

const requestAccountsBalance = async () => {
    cancelAllPendingRequests()

    renderAccountSelector.value = false
    applyErrorState.value = false

	try {
        await requestAccountsBalances()
        /* Setting Initial Selected Account */
		selectedAccountId.value = getAccountsBalances.value.find(account => { return account.default }).id

        renderAccountSelector.value = true
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, requestAccountsBalance)
	}
}

const requestOperationApprovals = async () => {
    cancelAllPendingRequests()

    paginationParams.value.pageNumber === 1 ? showLoader.value = true : tableLoadingMore.value = true
    applyErrorState.value = false

    try {
        await requestOperationApprovalsList({
            ...paginationParams.value,
            ...appliedFilters.value,
            endDate: createJSONDateEndDay(appliedFilters.value.endDate),
            status: 'SignedByMe',
            operationStatus: appliedFilters.value.operationStatus ?? getOperationsStatusList.value.map(status => status.id),
            accountIds: selectedAccountId.value
        })

        if (getOperationApprovalsList.value.length > 0) showFilters.value = true
        showLoader.value = false
        tableLoadingMore.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, requestOperationApprovals)
    }
}

const loadMoreHandler = () => {
    paginationParams.value.pageNumber++
    requestOperationApprovals()
}

/*************************************************
*                                                *
*            Operations Details Modal            *
*                                                *
*************************************************/
const operationDetailsInfo = ref({})

const filterSignatureList = (operation) => {
    if (operation.status.id === 3) {
        return operation.signatureList.filter(signature => signature.weight && signature.signDate).map(obj => ({ ...obj }))
    } else {
        return operation.signatureList.filter(signature => signature.weight).map(obj => ({ ...obj }))
    }
}

const openDetailsHandler = operation => {
    cancelAllPendingRequests()
    tableLoadingMore.value = false
    showOperationDetails.value = true
    operationDetailsInfo.value = {
        ...operation,
        signatureList: filterSignatureList(operation)
    }
}

initialRequest()

onBeforeUnmount(() => {
    clearOperationApprovalsList()
})
</script>

<style lang="scss" scoped>
.content-wrapper {
    @include commonEnteringAnimation;
    width: 100%;

    margin-top: 20px;
    padding: 0;

    &.with-filters {
        margin-top: 0;
    }
}
</style>
